import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import dayjs from 'dayjs';
import api from '../../services/api';

interface CreateEquipmentMaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  stores: { number: string; name: string }[];
  descriptions: string[];
  rgms: string[];
  areaCoaches: string[];
  regionalCoaches: string[];
}

interface SubCategory {
  id: number;
  name: string;
  requisition_category_id: number;
}

export default function CreateEquipmentMaintenanceModal({
  isOpen,
  onClose,
  onSubmit,
  stores,
  descriptions,
  rgms,
  areaCoaches,
  regionalCoaches,
}: CreateEquipmentMaintenanceModalProps) {
  const [formData, setFormData] = useState({
    date: dayjs().format('YYYY-MM-DD'),
    store: '',
    rgm: '',
    area_coach: '',
    regional_coach: '',
    description: '',
    category_id: 10,
    subcategory_id: '' as string | number,
    serial_number: '',
    quantity: 1
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  
  // const uniqueRgms = useMemo(() => {
  //   // Use hardcoded values if properties don't exist
  //   return ["MORGAN"]; // Based on your screenshot
  // }, []);

  // const uniqueAreaCoaches = useMemo(() => {
  //   // Use hardcoded values if properties don't exist
  //   return ["Strydom Naidoo"]; // Based on your screenshot
  // }, []);

  // const uniqueRegionalCoaches = useMemo(() => {
  //   // Use hardcoded values if properties don't exist
  //   return ["Strydom Naidoo"]; // Based on your screenshot
  // }, []);

  // const categories = useMemo(() => {
  //   // Get unique category values
  //   return ["NULL", "19", "22", "23"];
  // }, []);

  useEffect(() => {
    if (isOpen) {
      const fetchSubcategories = async () => {
        setLoadingSubcategories(true);
        try {
          const response = await api.get(`/equipment-maintenance/subcategories/10`);
          if (response.data && response.data.success) {
            setSubcategories(response.data.data);
          } else {
            console.error("Failed to fetch subcategories. API response issue. Data:", response.data, "Full response status:", response.status);
            setSubcategories([]);
          }
        } catch (err: any) {
          console.error('Error fetching subcategories. Request failed. Error object:', err);
          if (err.response) {
            console.error('Error response data:', err.response.data);
            console.error('Error response status:', err.response.status);
            // console.error('Error response headers:', err.response.headers);
          } else if (err.request) {
            console.error('Error request data:', err.request);
          } else {
            console.error('Error message:', err.message);
          }
          setSubcategories([]);
        } finally {
          setLoadingSubcategories(false);
        }
      };
      fetchSubcategories();
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.store) newErrors.store = 'Store is required';
    if (!formData.rgm) newErrors.rgm = 'RGM is required';
    if (!formData.area_coach) newErrors.area_coach = 'Area Coach is required';
    if (!formData.regional_coach) newErrors.regional_coach = 'Regional Coach is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.quantity || formData.quantity < 1) newErrors.quantity = 'Quantity must be at least 1';
    if (!formData.subcategory_id) newErrors.subcategory_id = 'Sub Category is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        ...formData,
        category_id: 10,
        subcategory_id: formData.subcategory_id ? Number(formData.subcategory_id) : null,
      };
      await onSubmit(dataToSubmit);
      setFormData({
        date: dayjs().format('YYYY-MM-DD'),
        store: '',
        rgm: '',
        area_coach: '',
        regional_coach: '',
        description: '',
        category_id: 10,
        subcategory_id: '',
        serial_number: '',
        quantity: 1
      });
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => !isSubmitting && onClose()}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen">
        <div className="fixed inset-0 bg-black opacity-30" />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full mx-4 shadow-xl">
          <div className="border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
            <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
              Add Equipment Maintenance Record
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              disabled={isSubmitting}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description*
                </label>
                <select
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a description</option>
                  {descriptions.map((desc) => (
                    <option key={desc} value={desc}>{desc}</option>
                  ))}
                </select>
                {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date*
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                />
                {errors.date && <p className="mt-1 text-sm text-red-500">{errors.date}</p>}
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Store*
                </label>
                <select
                  name="store"
                  value={formData.store}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.store ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a store</option>
                  {stores.map(store => (
                    <option key={store.number} value={store.number}>
                      {store.name}
                    </option>
                  ))}
                </select>
                {errors.store && <p className="mt-1 text-sm text-red-500">{errors.store}</p>}
              </div>

              {/* RGM Dropdown */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  RGM*
                </label>
                <select
                  name="rgm"
                  value={formData.rgm}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.rgm ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                >
                  <option value="">Select an RGM</option>
                  {rgms.map(rgm => (
                    <option key={rgm} value={rgm}>
                      {rgm}
                    </option>
                  ))}
                </select>
                {errors.rgm && <p className="mt-1 text-sm text-red-500">{errors.rgm}</p>}
              </div>

              {/* Area Coach Dropdown */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Area Coach*
                </label>
                <select
                  name="area_coach"
                  value={formData.area_coach}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.area_coach ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                >
                  <option value="">Select an Area Coach</option>
                  {areaCoaches.map(coach => (
                    <option key={coach} value={coach}>
                      {coach}
                    </option>
                  ))}
                </select>
                {errors.area_coach && <p className="mt-1 text-sm text-red-500">{errors.area_coach}</p>}
              </div>

              {/* Regional Coach Dropdown */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Regional Coach*
                </label>
                <select
                  name="regional_coach"
                  value={formData.regional_coach}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.regional_coach ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a Regional Coach</option>
                  {regionalCoaches.map(coach => (
                    <option key={coach} value={coach}>
                      {coach}
                    </option>
                  ))}
                </select>
                {errors.regional_coach && <p className="mt-1 text-sm text-red-500">{errors.regional_coach}</p>}
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Serial Number
                </label>
                <input
                  type="text"
                  name="serial_number"
                  value={formData.serial_number}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 dark:bg-gray-700 dark:text-white"
                  disabled={isSubmitting}
                />
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity*
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  className={`w-full rounded-md border ${errors.quantity ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting}
                />
                {errors.quantity && <p className="mt-1 text-sm text-red-500">{errors.quantity}</p>}
              </div>

              {/* Sub Category Dropdown */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sub Category*
                </label>
                <select
                  name="subcategory_id"
                  value={formData.subcategory_id}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.subcategory_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} px-3 py-2 dark:bg-gray-700 dark:text-white`}
                  disabled={isSubmitting || loadingSubcategories}
                >
                  <option value="">{loadingSubcategories ? 'Loading...' : 'Select a sub category'}</option>
                  {subcategories.map(subcat => (
                    <option key={subcat.id} value={subcat.id}>
                      {subcat.name}
                    </option>
                  ))}
                </select>
                {errors.subcategory_id && <p className="mt-1 text-sm text-red-500">{errors.subcategory_id}</p>}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="mr-3 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Record'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}